import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import {
    catchError,
    Observable,
    of,
    Subject,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs';
import { ServiceFulfillmentService } from '../../../../../../modules/service-fulfillment/services/service-fulfillment.service';
import { AdvisorCellRendererParams } from './models/advisor-cell-renderer-params';
import { DropdownOption } from '../../../../../../core/models/dropdown-option';
import { AdvisorFormModel } from './models/advisor-form-model';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { UpdateAdvisorRequest } from '../../../../../../modules/service-fulfillment/models/update-advisor-request';

@Component({
    selector: 'app-advisor-cell-renderer',
    imports: [MatSelectModule, MatFormFieldModule, ReactiveFormsModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './advisor-cell-renderer.component.html',
})
export class AdvisorCellRendererComponent
    implements ICellRendererAngularComp, OnDestroy
{
    private destroy$ = new Subject<void>();

    params!: AdvisorCellRendererParams;

    advisorDropdownOptions: DropdownOption[] = [];

    form: FormGroup<FormGroupType<AdvisorFormModel>> = this.getFormDefinition();

    constructor(
        private readonly serviceFulfillmentService: ServiceFulfillmentService,
    ) {}

    agInit(params: AdvisorCellRendererParams): void {
        this.params = params;

        this.bindForm(params);
        this.setupFormListeners();
        this.setupDropdownOptions();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    refresh(): boolean {
        return false;
    }

    private bindForm(params: AdvisorCellRendererParams): void {
        this.form.patchValue({
            userId: params.data.advisorId,
        });
    }

    private getFormDefinition(): FormGroup<FormGroupType<AdvisorFormModel>> {
        return new FormGroup<FormGroupType<AdvisorFormModel>>({
            userId: new FormControl<number | null>(null),
        });
    }

    private saveFormDataToApi(): Observable<void> {
        const request: UpdateAdvisorRequest = {
            applicationId: this.params.data.applicationId,
            advisorUserId: this.form.controls.userId.value,
        };
        return this.serviceFulfillmentService.updateApplicationAdvisor(request);
    }

    private setupFormListeners(): void {
        const userIdControl = this.form.controls.userId;
        let previousUserId: number | null = userIdControl.value;

        this.form.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                switchMap(() =>
                    this.saveFormDataToApi().pipe(
                        tap(() => {
                            const currentUserId = userIdControl.value!;
                            this.updateAdvisorAndRefreshGrid(currentUserId);
                            previousUserId = currentUserId;
                        }),
                        catchError(() => {
                            userIdControl.setValue(previousUserId, {
                                emitEvent: false,
                            });
                            return of(void 0);
                        }),
                    ),
                ),
            )
            .subscribe();
    }

    /**
     * Updates the advisor value for the current grid row
     * and refreshes the grid to reflect the change.
     *
     * @param advisorUserId - The new advisor userId to set
     */
    private updateAdvisorAndRefreshGrid(advisorUserId: number | null): void {
        this.params.data.advisorId = advisorUserId;
        this.params.data.isViewedByAdvisor = false;

        // Refresh the entire row to reflect the updated values and row style
        this.params.api.redrawRows({
            rowNodes: [this.params.node],
        });

        // Notify the grid that a filter change may have occurred
        this.params.api.onFilterChanged();
    }

    private setupDropdownOptions(): void {
        this.advisorDropdownOptions = this.params.dropdownOptions;
    }
}
